package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.dsl.ir.expr.ExprBlobReader
import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.ui.runtime.element.ModelPreviewInstanceKeys
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.element.UnstableModelPreviewKeyException
import top.wcpe.allincore.ui.runtime.element.VirtualTree
import top.wcpe.allincore.ui.runtime.internal.interop.reflectiveGetIdOrNull
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.lifecycle.LifecycleUtils
import top.wcpe.allincore.ui.runtime.reactive.Reactive
import top.wcpe.allincore.ui.runtime.reactive.ReactiveMap
import top.wcpe.allincore.ui.runtime.reactive.Signal

/**
 * 数据驱动 diff / 复用协作者：承载 source(list/map) 与 $for 的 keyed diff、模板实例化、
 * 虚拟列表单项实例化、key 提取与 ReactiveMap 字段级 patch。
 *
 * **依赖倒置（打破循环互递归）**：本协作者对「递归建树」唯一的回边——构建子元素——
 * 经注入函数 `buildChild: (UiNode, Context) -> UIElement<*>` 表达；调用方
 * （[ElementTreeBuilder]）把自己的 `::buildElement` 作实参传入。本协作者**不静态引用**
 * [ElementTreeBuilder]，从而消除编译期循环依赖。其余依赖（Context / ExprEvaluator /
 * ReactiveWrapper / Reactive / LifecycleUtils / [ElementOverlayAttacher]）照常直接调。
 */
@Suppress("TooManyFunctions", "LargeClass")
internal object DataDrivenDiffer {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.IrInterpreter")

    private fun containsModelPreview(nodes: List<UiNode>): Boolean = nodes.any { containsModelPreview(it) }

    private fun containsModelPreview(node: UiNode): Boolean =
        node.kind.equals("modelPreview", ignoreCase = true) || containsModelPreview(node.children)

    private fun requireStableModelPreviewKey(node: UiNode, key: Any?, index: Int) {
        if (!ModelPreviewInstanceKeys.isStableBusinessKey(key)) {
            throw UnstableModelPreviewKeyException(
                "[ui] 动态容器 ${node.id} 的模板子树包含 modelPreview，但 index=$index 未提供稳定业务 key；禁止使用瞬时 index 生成 instanceKey",
            )
        }
    }

    private fun duplicateModelPreviewKey(node: UiNode, key: Any?, index: Int): Nothing =
        throw UnstableModelPreviewKeyException(
            "[ui] 动态容器 ${node.id} 的模板子树包含 modelPreview，但 index=$index 出现重复业务 key「$key」；禁止降级为瞬时 index",
        )

    private fun resolveStrictKeys(
        node: UiNode,
        items: List<*>,
        resolve: (Any?, Int) -> Any?,
    ): List<Any?>? {
        if (!containsModelPreview(node.children)) return null
        val keys = ArrayList<Any?>(items.size)
        val seen = LinkedHashSet<Any?>()
        for ((index, item) in items.withIndex()) {
            val key = resolve(item, index)
            requireStableModelPreviewKey(node, key, index)
            if (!seen.add(key)) duplicateModelPreviewKey(node, key, index)
            keys.add(key)
        }
        return keys
    }

    private fun requireStrictMapKeys(node: UiNode, keys: Set<*>) {
        if (!containsModelPreview(node.children)) return
        for ((index, key) in keys.withIndex()) {
            requireStableModelPreviewKey(node, key, index)
        }
    }

    private fun validateVirtualBusinessKeys(templates: List<UiNode>, keys: List<*>, containerId: String?) {
        if (!containsModelPreview(templates)) return
        val seen = LinkedHashSet<Any?>()
        for ((index, key) in keys.withIndex()) {
            if (!ModelPreviewInstanceKeys.isStableBusinessKey(key)) {
                throw UnstableModelPreviewKeyException(
                    "[ui] 虚拟容器 $containerId 的模板子树包含 modelPreview，但 index=$index 未提供稳定业务 key；禁止使用瞬时 index 生成 instanceKey",
                )
            }
            if (!seen.add(key)) {
                throw UnstableModelPreviewKeyException(
                    "[ui] 虚拟容器 $containerId 的模板子树包含 modelPreview，但 index=$index 出现重复业务 key「$key」；禁止降级为瞬时 index",
                )
            }
        }
    }

    fun validateVirtualTreeBusinessKeys(
        templates: List<UiNode>,
        rows: List<VirtualTree.FlatRow>,
        containerId: String?,
    ) {
        if (!containsModelPreview(templates)) return
        validateVirtualBusinessKeys(templates, rows.map { it.node.id }, containerId)
    }

    private fun rejectIndexOnlyVirtualTemplate(templates: List<UiNode>, index: Int) {
        if (!containsModelPreview(templates)) return
        throw UnstableModelPreviewKeyException(
            "[ui] 虚拟容器模板子树包含 modelPreview，但 index=$index 仅有瞬时 index，必须提供稳定业务 key 才能生成 instanceKey",
        )
    }

    /**
     * M6-c：缓存"类是否存在 public `getId()` 方法"。
     *
     * 每次 List diff 都对每个 item 反射查 `getId()`，100 项 × 多次重建会造成可观开销；
     * 缓存仅记录 [Boolean]（true = 有，false = 无），命中后跳过反射并对应回退到
     * 索引 key。值为 true 时仍需调用 [Class.getMethod] 一次拿到
     * Method 实例并 invoke——但 JDK 内部对 Class 的方法表本身有缓存，热路径开销可接受。
     */

    /** source 求值结果按 List/Map 分派到对应 diff；其它类型记录警告并清空。 */
    fun rebuildChildren(
        container: UIElement<*>,
        node: UiNode,
        sourceValue: Any?,
        ctx: IrInterpreter.Context,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ) {
        if (sourceValue is List<*>) {
            diffListChildren(container, node, sourceValue, ctx, buildChild)
        } else if (sourceValue is Map<*, *>) {
            diffMapChildren(container, node, sourceValue, ctx, buildChild)
        } else {
            if (sourceValue != null) {
                LOGGER.warn(
                    "[ui] 数据驱动容器 ${node.id} 的 source 求值结果不是 List/Map（实际类型 ${sourceValue::class.simpleName}），渲染为空",
                )
            }
            disposeAllDataDrivenChildren(container, ctx)
            container.replaceChildren(emptyList<UIElement<*>>())
        }
    }

    /**
     * List diff：按 key 复用 UIElement 实例，通过 Signal.set 向复用实例注入新 item 值。
     * 新增 key → 实例化模板并记录形参 Signal；消失 key → dispose 整棵子树。
     */
    @Suppress("NestedBlockDepth", "CyclomaticComplexMethod", "ComplexCondition")
    fun diffListChildren(
        container: UIElement<*>,
        node: UiNode,
        items: List<*>,
        ctx: IrInterpreter.Context,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ) {
        var oldByKey = ctx.dataDrivenChildren[container]
        if (oldByKey == null) oldByKey = LinkedHashMap()
        var oldSignalsByKey = ctx.dataDrivenSignals[container]
        if (oldSignalsByKey == null) oldSignalsByKey = LinkedHashMap()
        // 字段级 ReactiveMap 缓存：普通 Map item 首次实例化时转换并存入，复用时直接 patch
        var oldRmByKey = ctx.dataDrivenReactiveMaps[container]
        if (oldRmByKey == null) oldRmByKey = LinkedHashMap()

        val newByKey = LinkedHashMap<Any?, UIElement<*>>()
        val newSignalsByKey = LinkedHashMap<Any?, MutableList<Signal<Any?>>>()
        val newRmByKey = LinkedHashMap<Any?, Any?>()
        val orderedChildren: MutableList<UIElement<*>> = ArrayList(items.size)
        val params: List<String> = node.sourceParams
        val strictKeys = resolveStrictKeys(node, items, ::extractListKey)

        var index = 0
        for (item in items) {
            var key: Any? = strictKeys?.get(index) ?: extractListKey(item, index)
            if (strictKeys == null && newByKey.containsKey(key)) {
                LOGGER.warn("[ui] 数据驱动容器 ${node.id} 发现重复 key「$key」（index=$index），降级为 @idx:$index 作为 diff key")
                key = IndexKey(index)
            }
            var el = oldByKey.remove(key)
            var signals: MutableList<Signal<Any?>>? = oldSignalsByKey.remove(key)
            val existingRm = oldRmByKey.remove(key)
            if (el == null) {
                // 新 key：将普通 Map item 转为 ReactiveMap，支持字段级细粒度响应
                signals = ArrayList(1)
                var instantiateItem: Any? = item
                if (item is Map<*, *> &&
                    item !is ReactiveMap &&
                    params.size >= 1 &&
                    "_" != params[0]
                ) {
                    val rm = ReactiveMap(item)
                    newRmByKey[key] = rm
                    instantiateItem = rm
                }
                el = instantiateTemplate(node, instantiateItem, key, index, ctx, signals, buildChild)
            } else {
                // key 命中复用：Map item 通过 ReactiveMap.put 字段级更新，非 Map 通过 Signal.set 回退
                if (params.size >= 1 && "_" != params[0] && !signals!!.isEmpty()) {
                    if (existingRm is ReactiveMap && item is Map<*, *>) {
                        // 字段级精准通知：ReactiveMap.put 内部以 Objects.equals 判等，未变化字段为空操作。
                        // 此处不调用 signals.get(0).set(...)：entry Signal 持有的是同一 ReactiveMap 实例，
                        // Objects.equals(ReactiveMap, ReactiveMap) 按值比较，即使字段变化也会短路，
                        // 导致通知永不触发。字段级更新必须绕过 Signal 直接调用 ReactiveMap.put。
                        patchReactiveMap(existingRm, item)
                        newRmByKey[key] = existingRm
                    } else {
                        signals[0].set(item)
                    }
                }
                // 注：params 是编译期 IR 常量，每次 diff 不变；
                // 若上方 if 条件为 false（参数为 _ 或空），则 existingRm 必为 null（创建时同样不满足转换条件），
                // 无需单独携带 existingRm 到 newRmByKey。
            }
            newByKey[key] = el
            newSignalsByKey[key] = signals!!
            orderedChildren.add(el)
            index++
        }
        // 仍留在 oldByKey 中的 key 是被淘汰的，递归释放其 lifecycleScope（不再被订阅）。
        // RFC-RECOMPOSITION：切换为 LifecycleUtils.disposeRecursively 后整棵子树资源全部释放。
        for (stale in oldByKey.values) {
            LifecycleUtils.disposeRecursively(stale)
        }
        ctx.dataDrivenChildren[container] = newByKey
        ctx.dataDrivenSignals[container] = newSignalsByKey
        ctx.dataDrivenReactiveMaps[container] = newRmByKey
        container.replaceChildren(orderedChildren)
    }

    /**
     * Map diff：默认 key 为 entry.getKey()，通过 Signal.set 向复用实例注入新 key/value。
     */
    @Suppress("NestedBlockDepth")
    fun diffMapChildren(
        container: UIElement<*>,
        node: UiNode,
        entries: Map<*, *>,
        ctx: IrInterpreter.Context,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ) {
        requireStrictMapKeys(node, entries.keys)
        var oldByKey = ctx.dataDrivenChildren[container]
        if (oldByKey == null) oldByKey = LinkedHashMap()
        var oldSignalsByKey = ctx.dataDrivenSignals[container]
        if (oldSignalsByKey == null) oldSignalsByKey = LinkedHashMap()

        val newByKey = LinkedHashMap<Any?, UIElement<*>>()
        val newSignalsByKey = LinkedHashMap<Any?, MutableList<Signal<Any?>>>()
        val orderedChildren: MutableList<UIElement<*>> = ArrayList(entries.size)
        val params: List<String> = node.sourceParams

        var index = 0
        for (entry in entries.entries) {
            val key: Any? = entry.key
            val value: Any? = entry.value
            var el = oldByKey.remove(key)
            var signals: MutableList<Signal<Any?>>? = oldSignalsByKey.remove(key)
            if (el == null) {
                signals = ArrayList(2)
                el = instantiateTemplateMap(node, key, value, index, ctx, signals, buildChild)
            } else {
                // 按形参顺序更新 Signal；key signal 调用 set(sameKey) 为内部幂等空操作
                var sigIdx = 0
                if (params.size >= 1 && "_" != params[0] && signals!!.size > sigIdx) {
                    signals[sigIdx++].set(key)
                }
                if (params.size >= 2 && "_" != params[1] && signals!!.size > sigIdx) {
                    signals[sigIdx].set(value)
                }
            }
            newByKey[key] = el
            newSignalsByKey[key] = signals!!
            orderedChildren.add(el)
            index++
        }
        for (stale in oldByKey.values) {
            LifecycleUtils.disposeRecursively(stale)
        }
        ctx.dataDrivenChildren[container] = newByKey
        ctx.dataDrivenSignals[container] = newSignalsByKey
        container.replaceChildren(orderedChildren)
    }

    /**
     * 为数据驱动容器 / $for 循环元素注册生命周期清理：元素自身 scope 释放时，
     * 从 Context 级映射（dataDrivenChildren / dataDrivenSignals / dataDrivenReactiveMaps）移除其条目。
     *
     * 解决嵌套数据驱动结构（如外层 $for 项内含内层 $for）随祖先子树 dispose 后，
     * 内层容器 / 循环元素在 Context 映射中残留孤儿强引用的泄漏：所有移除路径（diff 淘汰、
     * source 变 null、conditional 分支切换、virtualList 淘汰）最终都经
     * [LifecycleUtils.disposeRecursively] 释放 scope，由此统一触发本清理，
     * 无需在每个移除站点手工清理（避免遗漏 + 与生命周期解耦）。
     */
    fun registerCtxMapCleanup(element: UIElement<*>, ctx: IrInterpreter.Context) {
        element.lifecycleScope().register {
            ctx.dataDrivenChildren.remove(element)
            ctx.dataDrivenSignals.remove(element)
            ctx.dataDrivenReactiveMaps.remove(element)
        }
    }

    /** 释放容器下所有数据驱动子元素（用于 source 变为 null 等情况）。 */
    fun disposeAllDataDrivenChildren(container: UIElement<*>, ctx: IrInterpreter.Context) {
        val oldByKey = ctx.dataDrivenChildren.remove(container)
        ctx.dataDrivenSignals.remove(container)
        ctx.dataDrivenReactiveMaps.remove(container)
        if (oldByKey == null) return
        for (child in oldByKey.values) {
            LifecycleUtils.disposeRecursively(child)
        }
    }

    /**
     * List 形态默认 key 提取：
     * - [Map] 且含 "id" → 用其值
     * - POJO 含 getId() 方法 → 反射调用
     * - 否则回退到 [IndexKey]（容错；编译期 E705/E706 已经拦截大多数情况）
     *
     * 显式 `key = expr` 写法由调用方在调用本方法前处理（v28，[evaluateKeyExpr]）。
     */
    @Suppress("ReturnCount", "SwallowedException")
    fun extractListKey(item: Any?, index: Int): Any {
        if (item is Map<*, *>) {
            val id = item["id"]
            if (id != null) return id
            return IndexKey(index)
        }
        if (item == null) return IndexKey(index)
        reflectiveGetIdOrNull(item)?.let { return it }
        return IndexKey(index)
    }

    /**
     * 单参（List）模板实例化：locals = {param0 -> Signal(item)}。
     * 形参包装为 Signal，ExprEvaluator 自动展开并建立响应式追踪；
     * diff 复用时通过 Signal.set 注入新值令 Effect 重算，无需 dispose+重建。
     */
    fun instantiateTemplate(
        parent: UiNode,
        item: Any?,
        key: Any?,
        index: Int,
        ctx: IrInterpreter.Context,
        outSignals: MutableList<Signal<Any?>>,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ): UIElement<*> {
        val newLocals = LinkedHashMap<String, Any?>()
        val params: List<String> = parent.sourceParams
        if (params.size >= 1 && "_" != params[0]) {
            val sig: Signal<Any?> = Reactive.signal(item)
            newLocals[params[0]] = sig
            outSignals.add(sig)
        }
        return instantiateTemplateChildren(parent, newLocals, key, index, ctx, buildChild)
    }

    /** 双参（Map）模板实例化：locals = {param0 -> Signal(key), param1 -> Signal(value)}。 */
    fun instantiateTemplateMap(
        parent: UiNode,
        key: Any?,
        value: Any?,
        index: Int,
        ctx: IrInterpreter.Context,
        outSignals: MutableList<Signal<Any?>>,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ): UIElement<*> {
        val newLocals = LinkedHashMap<String, Any?>()
        val params: List<String> = parent.sourceParams
        if (params.size >= 1 && "_" != params[0]) {
            val keySig: Signal<Any?> = Reactive.signal(key)
            newLocals[params[0]] = keySig
            outSignals.add(keySig)
        }
        if (params.size >= 2 && "_" != params[1]) {
            val valSig: Signal<Any?> = Reactive.signal(value)
            newLocals[params[1]] = valSig
            outSignals.add(valSig)
        }
        return instantiateTemplateChildren(parent, newLocals, key, index, ctx, buildChild)
    }

    /**
     * 用绑定了模板形参的派生 ctx 实例化 parent.children；
     * 单子节点直接返回，多个子节点用透明 Box 包裹，确保返回单一 UIElement。
     */
    fun instantiateTemplateChildren(
        parent: UiNode,
        newLocals: Map<String, Any?>,
        key: Any?,
        itemIndex: Int,
        ctx: IrInterpreter.Context,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ): UIElement<*> {
        val derived = ctx.withDynamicItem(key, itemIndex).withEvalCtx(ctx.evalCtx.withLocals(newLocals))
        val templates: List<UiNode> = parent.children
        val built: UIElement<*>
        if (templates.size == 1) {
            built = buildChild(templates[0], derived.withTemplateNode(0, templates[0]))
        } else {
            // 多模板节点：包一层透明 box 维持「单实例」语义
            val wrapper: UIElement<*> = IrElementFactory.create("box")
            for ((templateIndex, template) in templates.withIndex()) {
                wrapper.child(buildChild(template, derived.withTemplateNode(templateIndex, template)))
            }
            built = wrapper
        }
        // 循环槽 tooltip 修复(P1+P2a):数据驱动重建路径不经过初始 attachElementOverlays,
        // 在此对实例化子树补挂 overlay 描述符 + 捕获 per-cell EvalContext(derived 含 cell 形参)。
        ElementOverlayAttacher.attachTemplateOverlays(built, derived)
        return built
    }

    /** 用模板实例化单个 virtualList 子项，注入 locals: {index = i}。 */
    fun instantiateVirtualItem(
        templates: List<UiNode>,
        index: Int,
        ctx: IrInterpreter.Context,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ): UIElement<*> {
        rejectIndexOnlyVirtualTemplate(templates, index)
        val locals = LinkedHashMap<String, Any?>()
        locals["index"] = index
        val derived = ctx.withDynamicItem(index, index).withEvalCtx(ctx.evalCtx.withLocals(locals))
        if (templates.size == 1) {
            return buildChild(templates[0], derived.withTemplateNode(0, templates[0]))
        }
        val wrapper: UIElement<*> = IrElementFactory.create("box")
        for ((templateIndex, template) in templates.withIndex()) {
            wrapper.child(buildChild(template, derived.withTemplateNode(templateIndex, template)))
        }
        return wrapper
    }

    /** 用模板实例化单个虚拟子项，注入自定义 locals（如 index / depth / data）。 */
    fun instantiateVirtualItem(
        templates: List<UiNode>,
        locals: Map<String, Any?>,
        ctx: IrInterpreter.Context,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ): UIElement<*> {
        val index = (locals["index"] as? Number)?.toInt() ?: 0
        val businessKey = locals["nodeId"]
        if (businessKey == null) {
            rejectIndexOnlyVirtualTemplate(templates, index)
        } else {
            validateVirtualBusinessKeys(templates, listOf(businessKey), null)
        }
        val dynamicKey = businessKey ?: index
        val derived = ctx.withDynamicItem(dynamicKey, index).withEvalCtx(ctx.evalCtx.withLocals(locals))
        if (templates.size == 1) {
            return buildChild(templates[0], derived.withTemplateNode(0, templates[0]))
        }
        val wrapper: UIElement<*> = IrElementFactory.create("box")
        for ((templateIndex, template) in templates.withIndex()) {
            wrapper.child(buildChild(template, derived.withTemplateNode(templateIndex, template)))
        }
        return wrapper
    }

    /**
     * $for List 形态 diff：按 key 增量 diff 子元素列表，并对复用实例做字段级响应式注入。
     *
     * key 来源优先级：显式 keyExprNode（v28）> 自动提取（[extractListKey]）。
     * 普通模板重复 key 时继续降级为 [IndexKey]；含 modelPreview 的模板改为在实例化前稳定失败。
     *
     * 复用时：普通 Map item 经 [patchReactiveMap] 字段级精准更新（未变字段零通知），
     * 非 Map item 通过 Signal.set 回退；index 可能因列表重排变化，一并更新。
     */
    @Suppress("NestedBlockDepth", "CyclomaticComplexMethod", "LongMethod", "ComplexCondition")
    fun diffForListChildren(
        container: UIElement<*>,
        node: UiNode,
        items: List<*>,
        keyExprNode: ExprNode?,
        ctx: IrInterpreter.Context,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ) {
        val forId: String = node.id
        var oldByKey = ctx.dataDrivenChildren[container]
        if (oldByKey == null) oldByKey = LinkedHashMap()
        var oldSignalsByKey = ctx.dataDrivenSignals[container]
        if (oldSignalsByKey == null) oldSignalsByKey = LinkedHashMap()
        var oldRmByKey = ctx.dataDrivenReactiveMaps[container]
        if (oldRmByKey == null) oldRmByKey = LinkedHashMap()

        val newByKey = LinkedHashMap<Any?, UIElement<*>>()
        val newSignalsByKey = LinkedHashMap<Any?, MutableList<Signal<Any?>>>()
        val newRmByKey = LinkedHashMap<Any?, Any?>()
        val orderedChildren: MutableList<UIElement<*>> = ArrayList(items.size)
        val hasVar2 = node.hasForVar2()
        val v1: String? = node.forVar1
        val v2: String? = if (hasVar2) node.forVar2 else null
        val strictKeys = resolveStrictKeys(node, items) { item, index ->
            if (keyExprNode != null) {
                evaluateKeyExpr(keyExprNode, node, item, index, forId, ctx)
            } else {
                extractListKey(item, index)
            }
        }

        var index = 0
        for (item in items) {
            var key: Any? = strictKeys?.get(index) ?: if (keyExprNode != null) {
                evaluateKeyExpr(keyExprNode, node, item, index, forId, ctx)
            } else {
                extractListKey(item, index)
            }
            // 重复 key 降级为 IndexKey(index)，保证 newByKey 与 orderedChildren 一一对应。
            // 所有 index 兜底统一用 IndexKey（见 extractListKey / evaluateKeyExpr），与自然 key 隔离命名空间。
            if (strictKeys == null && newByKey.containsKey(key)) {
                LOGGER.warn("[ui] for 容器 $forId 发现重复 key「$key」（index=$index），降级为 @idx:$index 作为 diff key")
                key = IndexKey(index)
            }
            var el = oldByKey.remove(key)
            var signals: MutableList<Signal<Any?>>? = oldSignalsByKey.remove(key)
            val existingRm = oldRmByKey.remove(key)
            if (el == null) {
                // 新 key：将普通 Map item 转为 ReactiveMap，支持字段级细粒度响应
                signals = ArrayList(2)
                var instantiateVar1: Any? = item
                if (item is Map<*, *> &&
                    item !is ReactiveMap &&
                    v1 != null &&
                    "_" != v1
                ) {
                    val rm = ReactiveMap(item)
                    newRmByKey[key] = rm
                    instantiateVar1 = rm
                }
                el = instantiateForTemplate(
                    node, instantiateVar1, hasVar2, index.toDouble(), key, index, ctx, signals, buildChild,
                )
            } else {
                // key 命中复用：Map item 通过 ReactiveMap.put 字段级更新，非 Map 通过 Signal.set 回退；
                // index 可能因列表重排而变化，一并更新。
                var sigIdx = 0
                if (v1 != null && "_" != v1 && signals!!.size > sigIdx) {
                    if (existingRm is ReactiveMap && item is Map<*, *>) {
                        // 字段级精准通知：entry Signal 持有的是同一 ReactiveMap 实例，
                        // Objects.equals(ReactiveMap, ReactiveMap) 按值比较会短路，
                        // 故字段级更新必须绕过 Signal 直接调用 ReactiveMap.put。
                        patchReactiveMap(existingRm, item)
                        newRmByKey[key] = existingRm
                    } else {
                        signals[sigIdx].set(item)
                    }
                    sigIdx++
                }
                if (hasVar2 && v2 != null && "_" != v2 && signals!!.size > sigIdx) {
                    signals[sigIdx].set(index.toDouble())
                }
            }
            newByKey[key] = el
            newSignalsByKey[key] = signals!!
            orderedChildren.add(el)
            index++
        }
        for (stale in oldByKey.values) {
            LifecycleUtils.disposeRecursively(stale)
        }
        ctx.dataDrivenChildren[container] = newByKey
        ctx.dataDrivenSignals[container] = newSignalsByKey
        ctx.dataDrivenReactiveMaps[container] = newRmByKey
        container.replaceChildren(orderedChildren)
    }

    /**
     * 为 $for List 形态构造普通局部变量映射（用于 key 表达式求值等不需要响应式的场景）。
     * var1→item, var2→(double)index；丢弃名 _ 不注入。
     */
    private fun buildForListLocals(node: UiNode, item: Any?, index: Int): Map<String, Any?> {
        val locals = LinkedHashMap<String, Any?>()
        val v1: String? = node.forVar1
        if (v1 != null && "_" != v1) locals[v1] = item
        if (node.hasForVar2()) {
            val v2: String? = node.forVar2
            if (v2 != null && "_" != v2) locals[v2] = index.toDouble()
        }
        return locals
    }

    /** 安全解码 keyExpr blob；普通模板解码异常时降级，含 modelPreview 的模板直接稳定失败。 */
    @Suppress("TooGenericExceptionCaught", "ReturnCount")
    fun parseKeyExprSafely(node: UiNode): ExprNode? {
        if (!node.hasKeyExpr()) return null
        return try {
            // hasKeyExpr() 已保证 keyExpr 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
            ExprBlobReader.readExpr(node.keyExpr!!)
        } catch (t: Throwable) {
            if (containsModelPreview(node.children)) {
                throw UnstableModelPreviewKeyException(
                    "[ui] for 容器 ${node.id} 的模板子树包含 modelPreview，但显式 key 表达式解码失败；必须提供可用的稳定业务 key",
                )
            }
            LOGGER.error("[ui] for 容器 ${node.id} 显式 key 表达式 blob 解码失败，降级为自动 key 提取：$t")
            null
        }
    }

    /**
     * 对单个 item 求值显式 key 表达式，结果为 null 或求值失败时降级为 [IndexKey]。
     */
    @Suppress("TooGenericExceptionCaught", "ReturnCount")
    private fun evaluateKeyExpr(
        keyExprNode: ExprNode,
        node: UiNode,
        item: Any?,
        index: Int,
        forId: String,
        ctx: IrInterpreter.Context,
    ): Any {
        val keyLocals = buildForListLocals(node, item, index)
        val keyCtx = ctx.evalCtx.withLocals(keyLocals)
        return try {
            val key: Any? = ExprEvaluator.eval(keyExprNode, keyCtx)
            if (key == null) {
                LOGGER.warn("[ui] for 容器 $forId key 表达式求值为 null（index=$index），降级为 @idx:$index")
                IndexKey(index)
            } else {
                key
            }
        } catch (t: Throwable) {
            LOGGER.warn("[ui] for 容器 $forId key 表达式求值失败（index=$index）：$t")
            IndexKey(index)
        }
    }

    /** $for Map 形态 diff：克隆 [diffMapChildren]，但循环变量来自 forVar1(key)/forVar2(value)。 */
    @Suppress("NestedBlockDepth", "ComplexCondition")
    fun diffForMapChildren(
        container: UIElement<*>,
        node: UiNode,
        entries: Map<*, *>,
        ctx: IrInterpreter.Context,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ) {
        requireStrictMapKeys(node, entries.keys)
        var oldByKey = ctx.dataDrivenChildren[container]
        if (oldByKey == null) oldByKey = LinkedHashMap()
        var oldSignalsByKey = ctx.dataDrivenSignals[container]
        if (oldSignalsByKey == null) oldSignalsByKey = LinkedHashMap()

        val newByKey = LinkedHashMap<Any?, UIElement<*>>()
        val newSignalsByKey = LinkedHashMap<Any?, MutableList<Signal<Any?>>>()
        val orderedChildren: MutableList<UIElement<*>> = ArrayList(entries.size)
        val hasVar2 = node.hasForVar2()
        val v1: String? = node.forVar1
        val v2: String? = if (hasVar2) node.forVar2 else null

        var index = 0
        for (entry in entries.entries) {
            val key: Any? = entry.key
            val value: Any? = entry.value
            var el = oldByKey.remove(key)
            var signals: MutableList<Signal<Any?>>? = oldSignalsByKey.remove(key)
            if (el == null) {
                signals = ArrayList(2)
                el = instantiateForTemplate(node, key, hasVar2, value, key, index, ctx, signals, buildChild)
            } else {
                // key 命中复用：map key 不变（Signal.set 幂等空操作），value 可能更新。
                var sigIdx = 0
                if (v1 != null && "_" != v1 && signals!!.size > sigIdx) {
                    signals[sigIdx++].set(key)
                }
                if (hasVar2 && v2 != null && "_" != v2 && signals!!.size > sigIdx) {
                    signals[sigIdx].set(value)
                }
            }
            newByKey[key] = el
            newSignalsByKey[key] = signals!!
            orderedChildren.add(el)
            index++
        }
        for (stale in oldByKey.values) {
            LifecycleUtils.disposeRecursively(stale)
        }
        ctx.dataDrivenChildren[container] = newByKey
        ctx.dataDrivenSignals[container] = newSignalsByKey
        container.replaceChildren(orderedChildren)
    }

    /**
     * $for 模板实例化：locals = {forVar1 -> Signal(var1Value) (, forVar2 -> Signal(var2Value))}。
     * 形参包装为 Signal，diff 复用时通过 Signal.set 注入新值而无需重建。
     * `_` 丢弃名不注入 locals，也不创建 Signal。
     */
    private fun instantiateForTemplate(
        node: UiNode,
        var1Value: Any?,
        hasVar2: Boolean,
        var2Value: Any?,
        key: Any?,
        itemIndex: Int,
        ctx: IrInterpreter.Context,
        outSignals: MutableList<Signal<Any?>>,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ): UIElement<*> {
        val newLocals = LinkedHashMap<String, Any?>()
        val v1: String? = node.forVar1
        if (v1 != null && "_" != v1) {
            val sig1: Signal<Any?> = Reactive.signal(var1Value)
            newLocals[v1] = sig1
            outSignals.add(sig1)
        }
        if (hasVar2) {
            val v2: String? = node.forVar2
            if (v2 != null && "_" != v2) {
                val sig2: Signal<Any?> = Reactive.signal(var2Value)
                newLocals[v2] = sig2
                outSignals.add(sig2)
            }
        }
        val el = instantiateTemplateChildren(node, newLocals, key, itemIndex, ctx, buildChild)
        // 元素自身释放时清理 Context 级映射条目（含嵌套子树，经 disposeRecursively 级联触发）
        registerCtxMapCleanup(el, ctx)
        return el
    }

    /**
     * 将 newData 的各字段逐一 put 到已有 ReactiveMap。
     *
     * [ReactiveMap.put] 内部以 [java.util.Objects.equals] 判等，
     * 未变化字段不触发任何订阅者，仅变化字段精准通知，实现字段级细粒度响应。
     *
     * **字段集稳定性前提：** 本方法只遍历 newData 的 key，
     * 不会删除 ReactiveMap 中已存在但 newData 中不包含的 key。
     * 调用方（服务端推送场景）需保证同一逻辑 item 在各次推送中字段集保持稳定；
     * 若服务端可能减少字段，需在上层先调用 [ReactiveMap.clear] 再 put。
     *
     * null key 经 [String.valueOf] 转为字符串 `"null"`，
     * 与 [ReactiveMap] 构造器行为一致。
     */
    @Suppress("UNCHECKED_CAST")
    private fun patchReactiveMap(rm: ReactiveMap, newData: Map<*, *>) {
        // 忠实保持原 Java 行为：value 经擦除写入，允许 null（LinkedHashMap 接受 null 值）。
        // ReactiveMap.put 的 Kotlin 签名为非空 value，但运行期底层 backing.put 接受 null，
        // 故按未检查转型为 MutableMap<String, Any?> 调用，与原 Java put(k, possiblyNull) 等价。
        val sink = rm as MutableMap<String, Any?>
        for (e in newData.entries) {
            // 等价于原 Java String.valueOf(e.getKey())：Kotlin 的 Any?.toString() 对 null 返回字符串 "null"。
            sink[e.key.toString()] = e.value
        }
    }

    /** List&lt;Map&gt; → List&lt;TreeNode&gt;。每项支持 id / children / 其余作为 data 透传。 */
    fun toTreeNodes(value: Any?): List<VirtualTree.TreeNode> {
        val result: MutableList<VirtualTree.TreeNode> = ArrayList()
        if (value !is List<*>) return result
        for (o in value) {
            if (o !is Map<*, *>) continue
            val id = if (o["id"] == null) "" else o["id"].toString()
            val childrenRaw = o["children"]
            val children: List<VirtualTree.TreeNode> = if (childrenRaw is List<*>) {
                toTreeNodes(childrenRaw)
            } else {
                emptyList()
            }
            val isLeaf = children.isEmpty()
            result.add(VirtualTree.TreeNode(id, isLeaf, o, children))
        }
        return result
    }

    /**
     * 专用 index-key 类型：普通模板的 index 兜底路径（重复 key 降级、[extractListKey] 无 id 回退、
     * [evaluateKeyExpr] 求值失败）统一返回它，与用户提供的自然 key（Map.id / getId() / keyExpr 结果）
     * 隔离命名空间，避免「数字 id 恰好等于某位置 index」时跨来源误碰撞 / 误复用。
     */
    private class IndexKey(val index: Int) {
        override fun equals(other: Any?): Boolean = other is IndexKey && other.index == this.index
        override fun hashCode(): Int = index.hashCode()
        override fun toString(): String = "@idx:$index"
    }
}
